import { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { CreditCard, Loader2, ShieldCheck, ArrowLeft, AlertCircle } from "lucide-react";
import { COMPANY } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SaveCardPage() {
  const [params] = useSearchParams();
  const bookingId = params.get("b");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!bookingId) { setError("Missing booking reference."); setLoading(false); return; }
    axios.get(`${API}/payments/booking-summary/${bookingId}`)
      .then((r) => setSummary(r.data))
      .catch(() => setError("We couldn't find that booking."))
      .finally(() => setLoading(false));
  }, [bookingId]);

  const saveCard = async () => {
    setStarting(true);
    try {
      const { data } = await axios.post(`${API}/payments/setup-card`, { booking_id: bookingId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setError(e.response?.data?.detail || "Could not start secure card setup. Please try again later.");
      setStarting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center px-5 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-[#F2B705] text-sm mb-6 transition-colors"><ArrowLeft size={16} /> Back to site</Link>
        <div className="flex items-center gap-3">
          <img src={COMPANY.logo} alt="Auto-Hire STL" className="h-14 w-14 object-contain rounded-md" />
          <div className="font-display font-extrabold text-2xl">AutoHire <span className="text-gold-gradient">STL</span></div>
        </div>

        <div className="mt-8 bg-[#0C0C0C] border border-white/10 rounded-2xl p-7" data-testid="save-card-page">
          {loading ? <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#F2B705]" /></div>
          : error ? (
            <div className="text-center py-6" data-testid="save-card-error"><AlertCircle className="text-red-400 mx-auto" size={40} /><p className="text-white/70 mt-3">{error}</p></div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-[#F2B705] text-sm font-bold uppercase tracking-widest"><ShieldCheck size={16} /> Secure Card on File</div>
              <h1 className="font-display font-black text-3xl mt-2">Save Your Card</h1>
              {summary && (
                <div className="mt-5 space-y-2 text-sm">
                  <Row label="Name" value={summary.name} />
                  <Row label="Vehicle" value={summary.vehicle} />
                  <Row label="Rental" value={`${summary.pickup_date} → ${summary.return_date}`} />
                </div>
              )}
              <p className="text-white/55 text-sm mt-5">Storing a card lets us quickly handle approved extras like tolls, tickets or damages — <b className="text-white">your card is not charged now</b> and is stored securely with Stripe.</p>
              <button data-testid="save-card-btn" onClick={saveCard} disabled={starting}
                className="mt-6 w-full bg-[#F2B705] text-black font-bold py-3.5 rounded-md hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {starting ? <Loader2 className="animate-spin" size={18} /> : <CreditCard size={18} />} Save Card Securely
              </button>
              <p className="text-white/35 text-xs text-center mt-3">Powered by Stripe. No charge is made when you save your card.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between"><span className="text-white/45">{label}</span><span className="text-white/85">{value}</span></div>
);
